import { useEffect, useMemo, useState } from 'react';
import { PCard } from './people';
import { Badge, Button, Icon, Mono } from './primitives';
import { downloadCsv } from '../lib/csv';
import { fmtDate } from '../lib/dates';
import { useOrgDates } from '../lib/orgSettings';
import { monthLabel } from '../lib/payrollPeriods';
import {
  byPeriod,
  explain,
  foldCompOff,
  ledgerSince,
  loadLedger,
  type LedgerRow,
  type LedgerSource,
} from '../lib/leaveLedger';

// How a leave balance got to its number, month by month.
//
// leave_balances is two running scalars with no date and no reason, so the
// question "where did my 8.5 casual days come from" had no answer anywhere —
// and audit_log could not stand in, because it is gated to org:full/people:full
// and therefore excludes the very person whose balance moved.
//
// Deliberately NOT an attention count (docs/UI-CONVENTIONS.md §1): nothing here
// is awaiting a decision. It is a record, so it carries no badge, no warning
// tone and no sidebar number.
//
// The Phase 3 critique of this card (it was never audited) found, and this
// fixes: a private map painting each source a hue, with HR's own adjustments in
// amber, the queue tone, on a record that waits on nobody; month disclosures
// with no expanded state and every month shut on arrival; three date formats,
// two from the browser's locale and one raw ISO key; a fourth hand-drawn
// outline button for Export; and a fixed four-track row that squeezed the
// explanation to nothing on a narrow card. Not fixed here, because the reader
// is outside this file: loadLedger() answers a failed read with [], so a
// failure still renders as "Nothing recorded yet".

// `accrual` is overtime that became leave days, which Org then called
// "Overtime → leave conversion": "Overtime leave", never "comp-off"
// (UI-CONVENTIONS, Vocabulary; critique 2026-10-05 #6). History only: since
// 20261022100000 comp off is a count of its own and writes no ledger row.
const SOURCE_LABEL: Record<LedgerSource, string> = {
  entitlement: 'Entitlement',
  accrual: 'Overtime leave',
  taken: 'Taken',
  returned: 'Returned',
  settlement: 'Settlement',
  adjustment: 'Adjustment',
};

/** `allotted` is what you were given, `used` is what you spent — a running
 *  balance has to net them, and showing them as one column would be a lie. */
const signed = (r: LedgerRow) => (r.field === 'used' ? -r.delta : r.delta);

// U+2212 for a loss, the glyph the money columns use; "+" for a gain. Zero is unsigned.
const fmt = (n: number) => {
  const v = Math.round(Math.abs(n) * 10) / 10;
  return `${n > 0 ? '+' : n < 0 ? '−' : ''}${v}`;
};

const NOTE = { fontSize: 'var(--hf-text-sm)', color: 'var(--hf-ink-3)' } as const;

export function LeaveTrail({
  profileId,
  types,
}: {
  profileId: string;
  /** id → label, so a movement names the leave type the org configured rather
   *  than its raw id. */
  types: Record<string, string>;
}) {
  const { dateFormat } = useOrgDates();
  const [rows, setRows] = useState<LedgerRow[] | null>(null);
  const [since, setSince] = useState<string | null>(null);
  // Which months are open. undefined until the read lands, then the newest
  // month: arriving to a list of shut rows told nobody how the balance was
  // reached, which is the card's whole job.
  const [open, setOpen] = useState<ReadonlySet<string> | undefined>(undefined);

  // No reset on a new profileId: the caller keys this card by person, so it
  // mounts fresh, loading, with no month opened by the last record.
  useEffect(() => {
    let live = true;
    void Promise.all([loadLedger(profileId), ledgerSince()]).then(([r, s]) => {
      if (!live) return;
      setRows(r);
      setSince(s);
    });
    return () => {
      live = false;
    };
  }, [profileId]);

  // The comp-off churn folded away, on screen and in the CSV alike.
  const shown = useMemo(() => foldCompOff(rows ?? []), [rows]);
  const periods = useMemo(() => byPeriod(shown), [shown]);
  const isOpen = (key: string) => (open ? open.has(key) : key === periods[0]?.[0]);
  const toggle = (key: string) => {
    const next = new Set(open ?? (periods[0] ? [periods[0][0]] : []));
    if (!next.delete(key)) next.add(key);
    setOpen(next);
  };

  const exportCsv = () =>
    downloadCsv(`hyprflow-leave-trail-${profileId.slice(0, 8)}.csv`, [
      ['Period', 'Date', 'Leave type', 'Movement', 'Days', 'Why'],
      ...shown.map((r) => [
        `${r.period_year}-${String(r.period_month).padStart(2, '0')}`,
        r.effective_on,
        types[r.leave_type_id] ?? r.leave_type_id,
        SOURCE_LABEL[r.source],
        signed(r),
        explain(r),
      ]),
    ]);

  const starts = since && (
    <>
      The trail starts <Mono>{fmtDate(since, dateFormat)}</Mono>.
    </>
  );

  return (
    <PCard
      label="How this balance was reached"
      right={
        rows?.length ? (
          <Button variant="ghost" size="sm" icon="download" onClick={exportCsv}>
            Export CSV
          </Button>
        ) : null
      }
    >
      {rows === null ? (
        <div style={NOTE}>Loading…</div>
      ) : rows.length === 0 ? (
        // Never a zero and never an empty table. There is no backfill, so a month
        // with no rows means "not captured", which is a different fact from
        // "nothing happened" and has to read as one.
        <div style={{ ...NOTE, lineHeight: 'var(--hf-leading-body)' }}>
          Nothing recorded yet.
          {starts && (
            <> {starts} Movements before then were not captured, and are not reconstructed here.</>
          )}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {periods.map(([key, movements]) => {
            const [y, m] = key.split('-').map(Number);
            const net = movements.reduce((s, r) => s + signed(r), 0);
            const shown = isOpen(key);
            const panel = `leave-trail-${profileId}-${key}`;
            return (
              <div key={key} style={{ borderBottom: '1px solid var(--hf-rule)' }}>
                <button
                  type="button"
                  aria-expanded={shown}
                  aria-controls={panel}
                  onClick={() => toggle(key)}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--hf-space-3)',
                    padding: 'var(--hf-space-3) 0',
                    background: 'transparent',
                    border: 0,
                    cursor: 'pointer',
                    textAlign: 'left',
                    font: 'inherit',
                    color: 'inherit',
                  }}
                >
                  <span style={{ flex: 1, fontSize: 'var(--hf-text-sm)', color: 'var(--hf-ink)' }}>
                    {monthLabel(y, m)}
                  </span>
                  {/* The sign carries direction; nothing here is a state to act
                      on, so nothing is toned. */}
                  <Mono style={{ color: net === 0 ? 'var(--hf-ink-3)' : 'var(--hf-ink)' }}>
                    {fmt(net)} days
                  </Mono>
                  <Icon
                    name={shown ? 'chevronDown' : 'chevronRight'}
                    size={13}
                    style={{ color: 'var(--hf-ink-3)' }}
                  />
                </button>

                {shown && (
                  <div
                    id={panel}
                    style={{
                      paddingBottom: 'var(--hf-space-3)',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 'var(--hf-space-2)',
                    }}
                  >
                    {movements.map((r) => (
                      <div
                        key={r.id}
                        style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          alignItems: 'baseline',
                          gap: 'var(--hf-space-1) var(--hf-space-3)',
                          paddingLeft: 'var(--hf-space-3)',
                          fontSize: 'var(--hf-text-sm)',
                          color: 'var(--hf-ink-2)',
                        }}
                      >
                        <Mono dim style={{ minWidth: '6.5rem' }}>
                          {fmtDate(r.effective_on, dateFormat)}
                        </Mono>
                        <Badge tone="neutral">{types[r.leave_type_id] ?? r.leave_type_id}</Badge>
                        {/* The text and its figure wrap as one: on their own, a
                            phone's row dropped the figure to a line by itself
                            under the text. */}
                        <span
                          style={{
                            flex: '1 1 14rem',
                            minWidth: 0,
                            display: 'flex',
                            alignItems: 'baseline',
                            gap: 'var(--hf-space-3)',
                          }}
                        >
                          <span style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>
                            <span
                              style={{
                                color: 'var(--hf-ink)',
                                fontWeight: 'var(--hf-weight-medium)',
                              }}
                            >
                              {SOURCE_LABEL[r.source]}
                            </span>
                            {' — '}
                            {explain(r)}
                          </span>
                          <Mono style={{ color: 'var(--hf-ink)' }}>{fmt(signed(r))}</Mono>
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {starts && (
            <div
              style={{
                fontSize: 'var(--hf-text-xs)',
                color: 'var(--hf-ink-3)',
                paddingTop: 'var(--hf-space-3)',
              }}
            >
              {starts} Anything before then was not captured.
            </div>
          )}
        </div>
      )}
    </PCard>
  );
}
